// Filename: HeatedPatioDogFriendlyBarsInVancouver.jsx

import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function HeatedPatioDogFriendlyBarsInVancouver() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "heated-patio-dog-friendly-bars-vancouver";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext();
  const ref_data = articles.find((art) => art.slug === ref_id);

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "section-1": "Tap & Barrel – Olympic Village",
    "section-2": "The Union",
    "section-3": "Bells & Whistles",
    "section-4": "The American",
    "section-5": "Craft Beer Market – False Creek",
    "section-6": "BREWHALL",
    "section-7": "Darby’s Public House",
    "section-8": "Batch! (Location 1)",
    "section-9": "Batch! (Location 2)",
    "section-10": "Steamworks Mount Pleasant",
    faq: "FAQ",
    "local-regulations": "Local Regulations",
    conclusion: "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  const referencesData = [
    {
      url: "https://vancouver.ca/your-government/animal-control-bylaw.aspx",
      label: "City of Vancouver – Dog Bylaws",
    },
    {
      url: "https://www.destinationvancouver.com/",
      label: "Tourism Vancouver",
    },
    {
      text: "Craft Beer Market (n.d.). 'Locations & Hours.' Official Website.",
    },
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  const locations = [
    {
      id: "section-1",
      lat: 49.27041,
      lng: -123.10868,
      popupText: "Tap & Barrel – Olympic Village",
    },
    {
      id: "section-2",
      lat: 49.27634,
      lng: -123.09871,
      popupText: "The Union",
    },
    {
      id: "section-3",
      lat: 49.25733,
      lng: -123.09171,
      popupText: "Bells & Whistles",
    },
    {
      id: "section-4",
      lat: 49.2773,
      lng: -123.10005,
      popupText: "The American",
    },
    {
      id: "section-5",
      lat: 49.2709,
      lng: -123.10982,
      popupText: "Craft Beer Market – False Creek",
    },
    {
      id: "section-6",
      lat: 49.26881,
      lng: -123.10138,
      popupText: "BREWHALL",
    },
    {
      id: "section-7",
      lat: 49.26615,
      lng: -123.1689,
      popupText: "Darby’s Public House",
    },
    {
      id: "section-8",
      lat: 49.274774681423594,
      lng: -123.110498746283,
      popupText: "Batch! (Location 1)",
    },
    {
      id: "section-9",
      lat: 49.27307096962068,
      lng: -123.15744451407404,
      popupText: "Batch! (Location 2)",
    },
    {
      id: "section-10",
      lat: 49.26501258598775,
      lng: -123.10125712726446,
      popupText: "Steamworks Mount Pleasant",
    },
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>Top 10 Dog-Friendly Bars with Heated Patios in Vancouver</title>
        <meta
          name="description"
          content="Explore the top dog-friendly bars in Vancouver that feature heated patios, ensuring a cozy outing for both you and your pup."
        />
        <meta
          name="keywords"
          content="dog-friendly bars, heated patios, Vancouver, pet-friendly, patios"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/heated-patio-dog-friendly-bars-vancouver.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="Top 10 Dog-Friendly Bars with Heated Patios in Vancouver"
        />
        <meta
          property="og:description"
          content="Explore the best dog-friendly bars in Vancouver with heated patios for a cozy outing."
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/heated-patio-dog-friendly-bars-vancouver.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/heated-patio-dog-friendly-bars-vancouver.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Top 10 Dog-Friendly Bars with Heated Patios in Vancouver"
        />
        <meta
          name="twitter:description"
          content="Cozy patios for you and your furry companion in Vancouver."
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/heated-patio-dog-friendly-bars-vancouver.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={
                ref_data?.image ||
                "https://bcouver.com/assets/images/article-fallback.jpg"
              }
              alt="A dog lounging on a heated patio in Vancouver."
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia />

            {/* SECTION: Introduction */}
            <h2 id="intro">Introduction</h2>
            <p>
              Vancouver’s mild coastal climate makes outdoor seating a
              year-round possibility—especially when there’s a heater nearby.
              For dog owners, finding a patio that welcomes furry friends can
              sometimes be tricky. In this guide, we’ll introduce you to ten
              fantastic bars in Vancouver that not only have heated patios but
              also welcome dogs with open arms.
            </p>

            {/* SECTION 1: Tap & Barrel – Olympic Village */}
            <h2 id="section-1">Tap & Barrel – Olympic Village</h2>
            <p
              ref={(el) => setSpotRef("section-1", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Tap & Barrel – Olympic Village</strong>{" "}
              <Marker
                coordY={49.27041}
                coordX={-123.10868}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              is located at 1 Athletes Way, offering scenic waterfront views
              along False Creek. With overhead heaters and wind-blocking panels,
              this patio stays cozy even on cooler days. Expect pub fare,
              craft beer, and an average meal cost between CAD 15–25. They are
              usually open from 11 a.m. until late, but check their website for
              exact hours or any seasonal changes.
            </p>

            {/* SECTION 2: The Union */}
            <h2 id="section-2">The Union</h2>
            <p
              ref={(el) => setSpotRef("section-2", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>The Union</strong>{" "}
              <Marker
                coordY={49.27634}
                coordX={-123.09871}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              can be found at 219 Union Street. Known for its Southeast
              Asian-inspired menu and eclectic cocktails, The Union keeps its
              outdoor area warm with radiant heaters. Most dishes fall in the
              CAD 10–20 range. Try their weekend brunch for a unique twist on
              classic favorites, and don’t forget to check in advance for dog
              policies during peak hours.
            </p>

            {/* SECTION 3: Bells & Whistles */}
            <h2 id="section-3">Bells & Whistles</h2>
            <p
              ref={(el) => setSpotRef("section-3", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Bells & Whistles</strong>{" "}
              <Marker
                coordY={49.25733}
                coordX={-123.09171}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              at 3296 Fraser Street is a casual haven for craft beer enthusiasts
              and sports fans. Their covered outdoor section is equipped with
              heaters, making it a cozy spot for you and your pup. With burgers,
              fries, and a rotating tap list, plan to spend around CAD 15–25 per
              person. It’s generally open daily from late morning to late night.
            </p>

            {/* SECTION 4: The American */}
            <h2 id="section-4">The American</h2>
            <p
              ref={(el) => setSpotRef("section-4", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>The American</strong>{" "}
              <Marker
                coordY={49.2773}
                coordX={-123.10005}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              is at 926 Main Street. With arcade games, sports screens, and a
              relaxed vibe, it’s an excellent spot to unwind with friends (both
              human and canine). The partially covered outdoor area includes
              heaters to keep you warm. You’ll find plenty of pub grub and an
              extensive beer list, with prices around CAD 15–20 per person. They
              typically open in the afternoon and close late.
            </p>

            {/* SECTION 5: Craft Beer Market – False Creek */}
            <h2 id="section-5">Craft Beer Market – False Creek</h2>
            <p
              ref={(el) => setSpotRef("section-5", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Craft Beer Market – False Creek</strong>{" "}
              <Marker
                coordY={49.2709}
                coordX={-123.10982}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              sits at 85 W 1st Avenue. Overlooking the picturesque False Creek,
              this bar is known for one of the largest craft beer selections in
              the city. The heated, covered patio typically welcomes dogs during
              non-peak times. Main meals range from CAD 15–30. Standard hours
              start at 11 a.m. and run until late, but always confirm before
              heading out.
            </p>

            {/* SECTION 6: BREWHALL */}
            <h2 id="section-6">BREWHALL</h2>
            <p
              ref={(el) => setSpotRef("section-6", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>BREWHALL</strong>{" "}
              <Marker
                coordY={49.26881}
                coordX={-123.10138}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              is a lively social house at 97 E 2nd Avenue. They brew fresh beer
              on-site, offer plenty of arcade-style games, and have an outdoor
              area with heaters to keep you and your dog comfortable. Expect
              brewpub staples like wings and pizzas, priced around CAD 15–25.
              They’re generally open from noon to midnight.
            </p>

            {/* SECTION 7: Darby’s Public House */}
            <h2 id="section-7">Darby’s Public House</h2>
            <p
              ref={(el) => setSpotRef("section-7", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Darby’s Public House</strong>{" "}
              <Marker
                coordY={49.26615}
                coordX={-123.1689}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              in Kitsilano (2001 Macdonald St) is a local favorite thanks to its
              spacious rooftop patio, which stays toasty with strategically
              placed heaters. You’ll find an impressive selection of local craft
              beers and classic pub fare around CAD 15–20 per person. Open daily
              from late morning to late evening, but check for any holiday or
              weekend variations.
            </p>

            {/* SECTION 8: Batch! (Location 1) */}
            <h2 id="section-8">Batch! (Location 1)</h2>
            <p
              ref={(el) => setSpotRef("section-8", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Batch! (Location 1)</strong>{" "}
              <Marker
                coordY={49.274774681423594}
                coordX={-123.110498746283}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              {" "}offers a heated patio area that keeps you and your four-legged
              companion comfortable, even in cooler weather. While the exact
              address may vary, the coordinates place this spot in the downtown
              Vancouver area. Expect a laid-back atmosphere with a rotating
              selection of craft beers. Operating hours can change seasonally,
              so it’s best to call ahead.
            </p>

            {/* SECTION 9: Batch! (Location 2) */}
            <h2 id="section-9">Batch! (Location 2)</h2>
            <p
              ref={(el) => setSpotRef("section-9", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Batch! (Location 2)</strong>{" "}
              <Marker
                coordY={49.27307096962068}
                coordX={-123.15744451407404}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              {" "}is another branch of the same establishment, situated farther
              west. They maintain a similar menu and welcoming policy for dogs
              on their heated patio. Be sure to keep your pet leashed and
              well-behaved, as space may be limited during busier hours.
            </p>

            {/* SECTION 10: Steamworks Mount Pleasant */}
            <h2 id="section-10">Steamworks Mount Pleasant</h2>
            <p
              ref={(el) => setSpotRef("section-10", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Steamworks Mount Pleasant</strong>{" "}
              <Marker
                coordY={49.26501258598775}
                coordX={-123.10125712726446}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              {" "}brings the well-known Steamworks brewing flair to the Mount
              Pleasant neighborhood. With a comfortable outdoor area featuring
              heaters, this spot is ideal for enjoying a pint alongside your
              canine companion. Their menu includes a variety of craft beers,
              and typical gastropub fare. Operating times often extend into the
              late evening, but check ahead for specific hours or any
              dog-friendly time restrictions.
            </p>

            {/* SECTION: FAQ */}
            <h2 id="faq">FAQ</h2>
            <div className="faq-section">
              <h3>1. Do all Vancouver bars allow dogs on patios?</h3>
              <p>
                No. While Vancouver is generally dog-friendly, individual bars
                and restaurants have their own policies. Always call ahead to
                verify.
              </p>

              <h3>2. Are service dogs exempt from these rules?</h3>
              <p>
                Yes. Certified service dogs typically have more access, but it’s
                still wise to carry any relevant documentation.
              </p>

              <h3>3. How do I ensure my dog is welcome?</h3>
              <p>
                Keep your dog on a leash and make sure they’re well-behaved.
                Avoid peak hours if your dog is sensitive to noise or crowds,
                and always bring water for your pup.
              </p>
            </div>

            {/* SECTION: Local Regulations */}
            <h2 id="local-regulations">Local Regulations</h2>
            <p>
              Vancouver requires dogs to be leashed in most public spaces,
              including patios, unless an off-leash policy is explicitly stated.
              Aggressive behavior or excessive barking can result in being asked
              to leave. Always check official resources or contact local
              authorities for the most current regulations.
            </p>
            <p>
              <strong>Disclaimer:</strong> Always verify a venue’s dog policy
              before visiting. Rules can change, and some patios may have
              seasonal restrictions. Consult local laws and official resources
              for the most accurate information.
            </p>

            {/* SECTION: Conclusion */}
            <h2 id="conclusion">Conclusion</h2>
            <p>
              From waterfront views to lively game spots, Vancouver has a
              variety of dog-friendly bars with heated patios. By following
              basic etiquette and confirming policies ahead of time, you and
              your pup can enjoy a warm, fun-filled outing in the city. Now,
              leash up and head out to explore these cozy patio gems!
            </p>

            {/* BOTTOM COMPONENT: references, author, related articles, etc. */}
            <Bottom referencesData={referencesData} ref_data={ref_data} />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay
        ref={mobileMapRef}
        markers={locations}
        spotRefs={spotRefs}
      />

      <Footer />
    </>
  );
}